/**
 * Access panel for a VM in the admin console.
 *
 * There is deliberately no reading of the customer's current password here: the admin API exposes
 * no such endpoint, and adding one would put every customer's live password one click away for
 * every operator. Resetting is the supported operator action, and the value it produces is shown
 * once, on the response that created it.
 */

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  AlertTriangle,
  Copy,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

import { adminResourceService } from "@/services/admin-resource.service";
import type { ResetVmPasswordResult } from "@/types/admin";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { APIError } from "@/lib/api";
import {
  VM_PASSWORD_FORBIDDEN_CHARACTERS,
  VM_PASSWORD_HINT,
  VM_PASSWORD_RULE_MESSAGES,
  validateVmPassword,
} from "@/lib/vmPassword";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogBody,
} from "@/components/ui/alert-dialog";
import { BusyOverlay } from "@/components/common/BusyOverlay";

interface VmPasswordResetPanelProps {
  resourceId: string;
  resourceName: string;
  /**
   * OS / template the VM was cloned from, e.g. "Windows-10-Pro" or "Ubuntu-24.04". Decides what the
   * panel says happens after a reset, which is the only thing that differs between the two.
   */
  operatingSystem?: string | null;
  /**
   * The VM's power state, e.g. "Running" or "Stopped". The provider refuses to write a password to
   * a machine that is off, so the panel blocks the attempt rather than letting the operator spend
   * two provider round trips on a refusal. Omitted or empty means unknown, and an unknown state is
   * allowed through — a missing prop must not take the button away.
   */
  status?: string | null;
}

export function VmPasswordResetPanel({
  resourceId,
  resourceName,
  operatingSystem,
  status,
}: VmPasswordResetPanelProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Which of the two states the confirm dialog is in. Named rather than inferred from an empty
  // input: a blank box reads as "not finished typing" just as readily as "generate one for me".
  const [passwordMode, setPasswordMode] = useState<"generate" | "custom">(
    "generate",
  );
  const [customPassword, setCustomPassword] = useState("");
  const [showCustomPassword, setShowCustomPassword] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  // Whether the password on screen is one the operator typed. The result copy differs entirely
  // between the two, and the radio can still be moved afterwards, so the result remembers its own.
  const [resultWasChosen, setResultWasChosen] = useState(false);
  // A failed reset, kept on screen rather than only in a toast. PASSWORD_APPLIED_NOT_STORED is the
  // reason it is an alert: the machine already holds a password nobody has a copy of, and the
  // recovery has a deadline the operator must still be able to read after the toast has gone.
  const [resetError, setResetError] = useState<{
    code: string;
    message: string;
  } | null>(null);
  // Whether the attempt that failed carried a password the operator typed. It decides which half of
  // the applied-but-not-stored copy is true, and the radio can still be moved afterwards.
  const [errorWasChosen, setErrorWasChosen] = useState(false);
  // Component state, not query cache: the password is returned once and can never be fetched again.
  const [result, setResult] = useState<ResetVmPasswordResult | null>(null);
  // Raised on success: the password is returned once and never again, so it gets a dialog of its
  // own rather than only the alert in the card, which an operator can scroll past.
  const [resultPromptOpen, setResultPromptOpen] = useState(false);

  // Which VM the state above belongs to. This panel lives on /resources/:id, and React Router swaps
  // the param without remounting anything — so when the next VM is already in the query cache the
  // detail page never falls back to its loading branch, this component keeps its place in the tree,
  // and `result` survives the navigation. An operator resetting several VMs in a row would then read
  // the previous VM's password under the new VM's name and hand it to the wrong customer. Resetting
  // on the prop change (React's documented "adjust state during render") keeps the guard inside the
  // component, where a caller cannot forget it, rather than relying on a `key` at the call site.
  const [shownFor, setShownFor] = useState(resourceId);
  if (shownFor !== resourceId) {
    setShownFor(resourceId);
    setResult(null);
    setResetError(null);
    setResultPromptOpen(false);
    setConfirmOpen(false);
    // The typed password goes with it. Carrying it to the next VM would let one customer's chosen
    // password be applied to another's machine without the operator retyping a thing.
    setPasswordMode("generate");
    setCustomPassword("");
    setShowCustomPassword(false);
    setPasswordTouched(false);
  }

  // Windows applies the new password without a restart; Linux reboots itself to pick it up. Either
  // way nobody has to touch the machine, so this panel states which of the two is happening instead
  // of offering a power action. Same test the backend uses to pick RDP over SSH.
  const isWindows = (operatingSystem ?? "").toLowerCase().includes("windows");

  // Measured on the dev webhook 2026-09-12: the provider accepted a cloud-init write on a running
  // VM over fifteen consecutive calls and refused it on all four stopped VMs tried, the same VM
  // flipping from refused to accepted when started. So this is a real precondition, not caution.
  // An explicit list, not `status !== "running"`. That inference swept in `Error` too, and an Error
  // row is almost always a record whose machine is gone — every one of the eight checked on dev
  // answered "Không tìm thấy UUID". Telling that operator to start the VM sends them to press Start
  // on a machine that does not exist, and hides the accurate `VM_NOT_FOUND_ON_PROVIDER` the backend
  // already returns. Only the states whose fix really is "start it and try again" are pre-empted
  // here; everything else goes down and gets the backend's own sentence.
  const blockedByPower =
    typeof status === "string" &&
    ["stopped", "suspended"].includes(status.toLowerCase());

  // Mirrors VmPasswordPolicy on the backend, so a refusal lands in the field rather than arriving
  // as a 400 after the operator has committed. The backend still decides.
  const customPasswordError = validateVmPassword(customPassword).errorCode;
  const canSubmit = passwordMode === "generate" || !customPasswordError;

  const resetMutation = useMutation({
    // The VM travels with the request. `shownFor` above clears this panel when the route param
    // changes, but a reset already in flight answers afterwards and would otherwise be attributed
    // to whichever VM is on screen by then — which is exactly how an operator hands one customer's
    // password to another.
    mutationFn: ({ id, password }: { id: string; password?: string }) =>
      adminResourceService.resetVmPassword(id, password),
    onSuccess: (data, variables) => {
      if (variables.id !== resourceId) {
        return;
      }

      setResult(data);
      setResultWasChosen(Boolean(variables.password));
      setResetError(null);
      setCustomPassword("");
      setShowCustomPassword(false);
      // The confirm has held the page for the length of the request; it hands over to the result
      // prompt in the same render, so there is never a moment with neither on screen.
      setConfirmOpen(false);
      setResultPromptOpen(true);
      toast.success(
        isWindows
          ? "Password reset. It works right away — no restart needed."
          : "Password reset. The VM will reboot itself to apply it.",
      );
    },
    onError: (error: Error, variables) => {
      if (variables.id !== resourceId) {
        return;
      }

      // The code, not just the sentence: PASSWORD_APPLIED_NOT_STORED means something entirely
      // different happened to the machine, and only the code says so. Every ErrorResponse reaches
      // here as an APIError carrying it; anything else (a thrown Error in a test, a bug) is a
      // plain failure.
      const code = error instanceof APIError ? error.code : "RESET_FAILED";
      const message = error.message || "Failed to reset the VM password.";

      setErrorWasChosen(Boolean(variables.password));
      setResetError({ code, message });
      // The alert below takes over from here; the confirm has nothing left to say.
      setConfirmOpen(false);
      toast.error(message);
    },
  });

  const copy = (value: string) => {
    navigator.clipboard.writeText(value);
    toast.success("Copied");
  };

  return (
    <div className="space-y-5">
      <Card className="border-border/80 shadow-none">
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5" />
              Access credentials
            </CardTitle>
            <CardDescription>
              Generate a new operating-system password for {resourceName}. The
              customer's current password is not shown here.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full shrink-0 sm:w-auto"
            disabled={resetMutation.isPending || blockedByPower}
            title={
              blockedByPower
                ? "The VM must be running before its password can be changed."
                : undefined
            }
            onClick={() => {
              // A fresh form every time: a password left over from the previous attempt could be
              // applied again without the operator seeing it.
              setPasswordMode("generate");
              setCustomPassword("");
              setShowCustomPassword(false);
              setPasswordTouched(false);
              setConfirmOpen(true);
            }}
          >
            {resetMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RotateCcw className="h-4 w-4" />
            )}
            Reset password
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {result ? (
            <Alert className="border-primary/30 bg-primary/5">
              <KeyRound className="h-4 w-4 text-primary" />
              <AlertTitle>New password</AlertTitle>
              <AlertDescription className="text-foreground">
                <p className="mb-3">
                  {resultWasChosen
                    ? "This is the password you set. It cannot be read back from here."
                    : "Copy it now — it is shown only once, and cannot be read back."}
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Input
                      value={result.username}
                      readOnly
                      className="font-mono"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-9 w-9 shrink-0"
                      onClick={() => copy(result.username)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      value={result.newPassword}
                      readOnly
                      className="font-mono"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-9 w-9 shrink-0"
                      onClick={() => copy(result.newPassword)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {/* Not keyed on requiresReboot: the backend sets it true unconditionally, and what
                    actually happens depends on the OS. Linux is the only one worth a warning
                    colour — the machine is about to go down on its own. */}
                {isWindows ? (
                  <p className="mt-3 text-sm text-muted-foreground">
                    The new password works right away — the machine does not
                    need restarting.
                  </p>
                ) : (
                  <p className="mt-3 flex items-start gap-2 text-sm font-medium text-warn">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    The VM will reboot itself to apply the new password. Wait
                    for it to come back up before handing the password over.
                  </p>
                )}
              </AlertDescription>
            </Alert>
          ) : (
            <p className="text-sm text-muted-foreground">
              A reset applies a new password to the VM's cloud-init
              configuration and replaces the stored copy the customer sees in
              their console. You can set the password yourself or let the system
              generate a strong one.
            </p>
          )}

          {/* Said before the attempt, not after it: nothing has failed here, so this keeps the
              plain colour and `destructive` is left to mean a reset that actually went wrong. */}
          {blockedByPower && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>The VM is not running</AlertTitle>
              <AlertDescription>
                This VM is {status}. The infrastructure will not write a new
                password to a machine that is off, so start it first and try
                again. Nothing is changed in the meantime — the customer keeps
                their current password.
              </AlertDescription>
            </Alert>
          )}

          {/* A failed reset, kept on screen. PASSWORD_APPLIED_NOT_STORED is the reason this is an
              alert and not just a toast: the VM already holds a password nobody has, and the
              message carries the one thing that recovers it — reset again, before the machine is
              restarted. */}
          {resetError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>
                {resetError.code !== "PASSWORD_APPLIED_NOT_STORED"
                  ? "Password not reset"
                  : errorWasChosen
                    ? "Password applied, but this console could not save it"
                    : "Password applied but not saved — act before the VM is restarted"}
              </AlertTitle>
              <AlertDescription>
                {/* The generated branch has lost the only copy of the password and the recovery has
                    a deadline. The chosen branch has lost nothing — the operator still holds what
                    they typed — so it must not be sent chasing a machine that is fine. */}
                {resetError.code === "PASSWORD_APPLIED_NOT_STORED" &&
                errorWasChosen
                  ? "The VM will use the password you entered from its next boot, so keep it. Until a reset succeeds, the customer’s own console will go on showing the previous password."
                  : resetError.message}
              </AlertDescription>
            </Alert>
          )}

          {/* Operators reach this tab to solve somebody else's problem on the phone, so what the
              button does, what it cannot do, and what the customer has to do next all have to be
              readable without leaving the page. */}
          <div className="rounded-md border border-border/80 bg-muted/30 p-3">
            <p className="mb-2 text-sm font-medium">How this works</p>
            <ol className="list-decimal space-y-1.5 pl-5 text-sm text-muted-foreground">
              <li>
                The new password is written into the VM's cloud-init
                configuration at the infrastructure — WiCloud does not touch the
                guest directly.
              </li>
              <li>
                <span className="font-medium text-foreground">
                  The VM must be running.
                </span>{" "}
                The provider refuses the write on a stopped or suspended
                machine, and nothing is applied at all.
              </li>
              <li>
                Either the system generates a 12-character password, or you set
                your own: 12–64 characters with an upper-case letter, a
                lower-case letter and a digit, no spaces and none of{" "}
                <code className="font-mono">
                  {VM_PASSWORD_FORBIDDEN_CHARACTERS.split("").join(" ")}
                </code>{" "}
                — those break when the password is pasted into a shell.
              </li>
              <li>
                The password is shown{" "}
                <span className="font-medium text-foreground">once</span>, on
                the response that created it. It cannot be read back afterwards
                by anyone, including you — a lost password means another reset.
              </li>
              <li>
                {isWindows
                  ? "Windows picks the new password up without restarting."
                  : "Linux is rebooted by the provider itself to apply it, so the machine goes down briefly."}
              </li>
              <li>
                The copy the customer sees in their own console is replaced at
                the same time, so support and customer stay in step.
              </li>
            </ol>
          </div>
        </CardContent>
      </Card>

      {/* Raised the moment a reset succeeds. The power cycle is the half of the job that makes the
          new password real, and it is the half an operator forgets — so it is offered here, in the
          same breath, rather than left to be found under the actions menu. */}
      {/* Raised the moment a reset succeeds. The password is returned once and never again, so it
          gets a dialog of its own — and the dialog says what happens next, which differs by OS and
          is the part an operator would otherwise have to know from memory. */}
      <AlertDialog open={resultPromptOpen} onOpenChange={setResultPromptOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>New password for {resourceName}</AlertDialogTitle>
          </AlertDialogHeader>

          <AlertDialogBody className="space-y-4">
            <AlertDialogDescription>
              {isWindows
                ? "The new password works right away — the machine does not need restarting."
                : "The VM will reboot itself to apply the new password. Wait for it to come back up before handing the password over."}
            </AlertDialogDescription>
            {result && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  {resultWasChosen
                    ? "This is the password you set. It cannot be read back from here."
                    : "Copy the password before you leave this page — it cannot be read back."}
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    value={result.newPassword}
                    readOnly
                    className="font-mono"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    onClick={() => copy(result.newPassword)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </AlertDialogBody>

          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          // Only the mutation closes this while it runs; see the action below.
          if (open || !resetMutation.isPending) setConfirmOpen(open);
        }}
      >
        <AlertDialogContent
          onEscapeKeyDown={(event) => {
            // The page overlay blocks the mouse, but only this dialog's focus trap stops a keyboard
            // user from reaching the tabs — and leaving the Access tab unmounts this panel, taking
            // the one readable copy of the new password with it.
            if (resetMutation.isPending) {
              event.preventDefault();
            }
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Reset the VM password?</AlertDialogTitle>
          </AlertDialogHeader>

          <AlertDialogBody className="space-y-4">
            <AlertDialogDescription>
              This replaces the password for {resourceName} and the copy the
              customer sees.{" "}
              {isWindows
                ? "The current password stops working as soon as the new one is applied."
                : "The VM reboots itself to apply it, so the customer keeps working with the old password until it comes back up."}
            </AlertDialogDescription>
            {/* Both outcomes are named. An optional box left empty would be ambiguous — it reads as
              "not finished typing" as readily as "generate one for me". */}
            <RadioGroup
              value={passwordMode}
              onValueChange={(value) => {
                setPasswordMode(value as "generate" | "custom");
                setPasswordTouched(false);
              }}
              disabled={resetMutation.isPending}
              className="gap-3"
            >
              <label
                htmlFor="admin-vm-password-generate"
                className="flex cursor-pointer items-start gap-3"
              >
                <RadioGroupItem
                  value="generate"
                  id="admin-vm-password-generate"
                  className="mt-0.5 cursor-pointer"
                />
                <span className="text-sm leading-snug">
                  Let the system create a strong password (recommended)
                </span>
              </label>
              <label
                htmlFor="admin-vm-password-custom"
                className="flex cursor-pointer items-start gap-3"
              >
                <RadioGroupItem
                  value="custom"
                  id="admin-vm-password-custom"
                  className="mt-0.5 cursor-pointer"
                />
                <span className="text-sm leading-snug">
                  Set the password myself
                </span>
              </label>
            </RadioGroup>

            {passwordMode === "custom" && (
              <div className="space-y-2">
                <div className="relative">
                  <Input
                    type={showCustomPassword ? "text" : "password"}
                    value={customPassword}
                    autoFocus
                    autoComplete="new-password"
                    disabled={resetMutation.isPending}
                    placeholder="At least 12 characters"
                    onChange={(event) => {
                      setCustomPassword(event.target.value);
                      setPasswordTouched(true);
                    }}
                    className="pr-10 font-mono"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-9 w-9"
                    disabled={resetMutation.isPending}
                    aria-label={
                      showCustomPassword ? "Hide password" : "Show password"
                    }
                    onClick={() => setShowCustomPassword(!showCustomPassword)}
                  >
                    {showCustomPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                {/* The rules stand where the failure will appear, so the operator reads what is
                  wanted before being told they got it wrong. */}
                <p
                  className={
                    passwordTouched && customPasswordError
                      ? "text-xs text-destructive"
                      : "text-xs text-muted-foreground"
                  }
                >
                  {passwordTouched && customPasswordError
                    ? VM_PASSWORD_RULE_MESSAGES[customPasswordError]
                    : VM_PASSWORD_HINT}
                </p>
              </div>
            )}
          </AlertDialogBody>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={resetMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={resetMutation.isPending || !canSubmit}
              onClick={(event) => {
                // Radix closes the dialog on click. Held open instead, so it (and the overlay) stay
                // up for the length of the request rather than the confirm vanishing before the
                // operator knows whether a password exists.
                event.preventDefault();
                resetMutation.mutate({
                  id: resourceId,
                  password:
                    passwordMode === "custom" ? customPassword : undefined,
                });
              }}
            >
              {resetMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Reset password
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BusyOverlay
        open={resetMutation.isPending}
        label="Resetting VM password…"
      />
    </div>
  );
}
